import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Slide,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Edit, Delete, Search } from "@mui/icons-material";
import AdminSidebar from "./AdminSidebar";
import axios from "axios";

// Import BASE_URL from your backend configuration
import { BASE_URL } from "../backend/backend.js";

const DoctorManager = () => {
  const [doctors, setDoctors] = useState([]);
  const [formData, setFormData] = useState({
    id: 0,
    username: "", 
    email: "",    
    mobileNumber: "", 
    roles: [],    
    enabled: false, 
  });
  const [editIndex, setEditIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      // Replaced hardcoded URL with BASE_URL
      const response = await axios.get(`${BASE_URL}/admin/getAllDoctor`, {
        withCredentials: true,
      });
      console.log("API Response: ", response.data); 

      if (response.data && Array.isArray(response.data.body)) {
        setDoctors(response.data.body.map(doctor => ({
          id: doctor.id,
          username: doctor.username,
          email: doctor.email,
          mobileNumber: doctor.mobileNumber,
          roles: doctor.roles,
          enabled: doctor.enabled,
        })));
      } else {
        console.error("Unexpected response format for doctors: ", response.data);
      }
    } catch (error) {
      console.error("Error fetching doctor data:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddOrUpdate = async () => {
    try {
      if (formData.id === 0) {
        // Add new doctor logic
        console.log("Adding new doctor:", formData);
        // await axios.post(`${BASE_URL}/admin/saveDoctor`, formData, {
        //   withCredentials: true,
        // });
      } else {
        // Edit existing doctor - Replaced hardcoded URL with BASE_URL
        await axios.put(`${BASE_URL}/admin/updateDoctor/${formData.id}`, {
          username: formData.username,
          email: formData.email,
          mobileNumber: formData.mobileNumber,
          enabled: formData.enabled,
        }, {
          withCredentials: true,
        });
      }
      fetchDoctors();
      setFormData({
        id: 0,
        username: "",
        email: "",
        mobileNumber: "",
        roles: [],
        enabled: false,
      });
      setEditIndex(null);
    } catch (error) {
      console.error("Error saving/updating doctor:", error);
    }
  };

  const handleEdit = (index) => {
    const doctorToEdit = doctors[index];
    setFormData({
      id: doctorToEdit.id,
      username: doctorToEdit.username,
      email: doctorToEdit.email || "",
      mobileNumber: doctorToEdit.mobileNumber || "",
      roles: doctorToEdit.roles || [],
      enabled: doctorToEdit.enabled || false,
    });
    setEditIndex(index);
  };

  const handleDelete = async (index) => {
    try {
      const id = doctors[index].id;
      // Replaced hardcoded URL with BASE_URL
      const response = await axios.delete(`${BASE_URL}/admin/deleteDoctor/${id}`, {
        withCredentials: true,
      });

      if (response.status === 200) {
        setDoctors((prevDoctors) => prevDoctors.filter((_, idx) => idx !== index));
      } else {
        console.error("Error deleting doctor:", response);
      }
    } catch (error) {
      console.error("Error deleting doctor:", error);
    }
  };

  const filteredDoctors = doctors.filter((doc) =>
    (doc.username || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (doc.email || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AdminSidebar />
      <Box sx={{ flexGrow: 1, padding: 4 }}>
        <Typography variant="h4" sx={{ textAlign: "center", marginBottom: 4 }}>
          Doctor Management
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ padding: 3 }}>
              <Typography variant="h6" gutterBottom>
                {editIndex !== null ? "Edit Doctor" : "Add New Doctor"}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Mobile Number"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="enabled"
                        checked={formData.enabled}
                        onChange={handleChange}
                      />
                    }
                    label="Enabled"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color={editIndex !== null ? "warning" : "primary"}
                    onClick={handleAddOrUpdate}
                    fullWidth
                  >
                    {editIndex !== null ? "Update Doctor" : "Add Doctor"}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Slide direction="down" in={true} mountOnEnter unmountOnExit>
              <Box mb={2}>
                <TextField
                  label="Search by Username or Email"
                  variant="outlined"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Slide>
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Mobile Number</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDoctors.map((doc, index) => (
                      <TableRow key={doc.id}>
                        <TableCell>{doc.id}</TableCell>
                        <TableCell>{doc.username}</TableCell>
                        <TableCell>{doc.email}</TableCell>
                        <TableCell>{doc.mobileNumber}</TableCell>
                        <TableCell>{doc.enabled ? "Active" : "Inactive"}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton color="primary" onClick={() => handleEdit(index)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton color="error" onClick={() => handleDelete(index)}>
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredDoctors.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No doctor found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default DoctorManager;